import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle, ChevronDown, XCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Textarea } from '@/app/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  usePayrollRun,
  useFinalizePayrollRun,
  useReopenPayrollRun,
  useCreatePayrollAdjustment,
  usePayrollRunSnapshots,
  useExportPayrollRun,
  useUnassignedPayrollAdjustments,
  type PayrollExportError,
} from '@/hooks/use-payroll';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { formatMoney } from '@/lib/currency';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { rateTypeLabel } from '@/lib/payroll-rate-type';
import type { PayrollAdjustment } from '@/lib/types';
import { JobBreakdownDisclosure, overtimeMethodLabel } from './job-breakdown-disclosure';

type PayrollExportFormat = 'csv' | 'quickbooks_iif' | 'xero_csv' | 'gusto_csv' | 'adp_csv';
type UnratedWorkRow = {
  worker: string;
  person_id: number | null;
  project_id: number | null;
  rate_type: 'hourly' | 'daily';
  date: string;
  project_name: string;
  hours: string;
};

const EXPORT_OPTIONS: { value: PayrollExportFormat; label: string; extension: string }[] = [
  { value: 'csv', label: 'Generic CSV', extension: 'csv' },
  { value: 'quickbooks_iif', label: 'QuickBooks IIF', extension: 'iif' },
  { value: 'xero_csv', label: 'Xero CSV', extension: 'csv' },
  { value: 'gusto_csv', label: 'Gusto CSV', extension: 'csv' },
  { value: 'adp_csv', label: 'ADP CSV', extension: 'csv' },
];

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  finalized: 'bg-green-100 text-green-800',
  exported: 'bg-purple-100 text-purple-800',
};
const MINIMUM_TWO_FRACTION_DIGITS = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
} satisfies Intl.NumberFormatOptions;

export function PayrollRunDetailPage() {
  const orgCurrency = useOrgCurrency();
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const runId = Number(id) || 0;
  // Run management (finalize, reopen, export, adjust) is manager+ only; members
  // who reach a run by URL see it read-only. Data access itself is enforced
  // server-side (Security S5).
  const { canManagePayroll } = usePermissions();

  const { data: run, isLoading } = usePayrollRun(runId);
  const finalizeRun = useFinalizePayrollRun();
  const reopenRun = useReopenPayrollRun();
  const createAdjustment = useCreatePayrollAdjustment();
  const { data: snapshots = [] } = usePayrollRunSnapshots(runId);
  const exportRun = useExportPayrollRun();
  const { data: unassignedAdjustments = [] } = useUnassignedPayrollAdjustments();
  const finalizeError = (
    finalizeRun.error as {
      response?: {
        data?: {
          error?: string;
          unrated_work?: UnratedWorkRow[];
        };
      };
    } | null
  )?.response?.data;

  const [exportError, setExportError] = useState<PayrollExportError | null>(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenAcknowledged, setReopenAcknowledged] = useState(false);
  const [adjustmentLineItem, setAdjustmentLineItem] = useState('');
  const [adjustmentField, setAdjustmentField] = useState<PayrollAdjustment['field']>('regular_hours');
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');

  const handleApprove = () => {
    finalizeRun.mutate(runId);
  };

  const handleExport = (format: PayrollExportFormat) => {
    const option = EXPORT_OPTIONS.find((o) => o.value === format);
    const extension = option?.extension ?? 'csv';
    setExportError(null);
    exportRun.mutate(
      { id: runId, format },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `payroll_run_${runId}_${format}.${extension}`;
          a.click();
          URL.revokeObjectURL(url);
        },
        onError: (error) => {
          setExportError(error as PayrollExportError);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">Loading payroll run...</div>
    );
  }

  if (!run) {
    return (
      <div className="p-8 text-center text-gray-500">Payroll run not found.</div>
    );
  }

  const outgoingAdjustments = run.outgoing_adjustments ?? [];
  const activeOutgoingAdjustments = outgoingAdjustments.filter(
    (adjustment) => !adjustment.is_superseded,
  );
  const supersededAdjustments = outgoingAdjustments.filter(
    (adjustment) => adjustment.is_superseded,
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {unassignedAdjustments.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Unassigned prior-period adjustments</AlertTitle>
          <AlertDescription>
            <div className="space-y-1">
              {unassignedAdjustments.map((adjustment) => (
                <div key={adjustment.id}>
                  {adjustment.worker_name} · {adjustment.field_label} {adjustment.delta} ·
                  Run #{adjustment.original_run} ({adjustment.original_run_pay_period_name}) ·
                  {adjustment.reason}
                </div>
              ))}
              <p>These will be attached automatically to the next pay period run you create.</p>
            </div>
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/payroll/runs')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {run.pay_period_name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={statusColors[run.status] ?? 'bg-gray-100 text-gray-800'}>
                {run.status}
              </Badge>
              {run.finalized_by_username && (
                <span className="text-sm text-gray-500">
                  Finalized by {run.finalized_by_username} at{' '}
                  {run.finalized_at ? formatDateTimeInTimeZone(run.finalized_at, timeZone) : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        {canManagePayroll && (
        <div className="flex gap-2">
          {run.status === 'draft' && (
            <Button onClick={handleApprove} disabled={finalizeRun.isPending}>
              <CheckCircle className="h-4 w-4 mr-2" />
              {finalizeRun.isPending ? 'Finalizing...' : 'Finalize'}
            </Button>
          )}
          {run.is_locked && (
            <Button
              variant="outline"
              data-validate-required="true"
              data-validation-scope="payroll-reopen-fields"
              onClick={() => {
                if (!reopenReason.trim()) return;
                reopenRun.mutate({ id: runId, reason: reopenReason });
              }}
              disabled={reopenRun.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reopen
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exportRun.isPending}>
                <Download className="h-4 w-4 mr-2" />
                {exportRun.isPending ? 'Exporting...' : 'Export'}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {EXPORT_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleExport(option.value)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        )}
      </div>

      {finalizeRun.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-red-800">
                {finalizeError?.error || 'Unable to finalize payroll run.'}
              </p>
              {finalizeError?.unrated_work && finalizeError.unrated_work.length > 0 && (
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  {finalizeError.unrated_work.map((row, index) => (
                    <li key={`${row.person_id}-${row.rate_type}-${row.date}-${row.project_id}-${index}`}>
                      {row.worker} — {row.date} — {row.project_name} — {row.hours} hours
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {exportError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-red-800">
                {exportError.message || 'Export failed'}
              </p>
              {exportError.missing_employees && exportError.missing_employees.length > 0 && (
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  {exportError.missing_employees.map((emp) => (
                    <li key={`${emp.person_id}-${emp.field}`}>
                      {emp.user_id ? (
                        <Link
                          to={`/people/${emp.user_id}/edit`}
                          className="underline hover:text-red-900"
                        >
                          {emp.name}
                        </Link>
                      ) : (
                        <span>{emp.name}</span>
                      )}
                      {' — missing '}
                      {emp.field === 'adp_file_number' ? 'ADP File Number' : 'Xero Employee Number'}
                    </li>
                  ))}
                </ul>
              )}
              {exportError.adjustment_ids && exportError.adjustment_ids.length > 0 && (
                <p className="text-sm text-red-700">
                  Blocked adjustments: {exportError.adjustment_ids.map((id) => `#${id}`).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {canManagePayroll && run.is_locked && (
        <Card>
          <CardHeader><CardTitle>Reopen payroll run</CardTitle></CardHeader>
          <CardContent id="payroll-reopen-fields" className="space-y-3">
            {run.status === 'exported' && (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold">This run has already been exported — the payroll provider has received the earlier file.</p>
                <p>Reopening keeps that file and snapshot as history; any re-export will produce a second file.</p>
                <label
                  className="mt-2 flex items-center gap-2"
                  data-required="true"
                  data-required-empty={reopenAcknowledged ? 'false' : 'true'}
                  data-required-message="Acknowledge the previous export."
                  tabIndex={-1}
                >
                  <input type="checkbox" checked={reopenAcknowledged} onChange={(e) => setReopenAcknowledged(e.target.checked)} />
                  I understand
                </label>
              </div>
            )}
            <Textarea
              placeholder="Reason for reopening (required)"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              required
            />
            {reopenRun.isError && (
              <p className="text-sm text-red-700">
                {(reopenRun.error as { response?: { data?: { error?: string } } }).response?.data?.error
                  ?? 'Unable to reopen payroll run.'}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {(run.is_locked || run.adjustments?.length > 0 || run.outgoing_adjustments?.length > 0) && (
        <Card>
          <CardHeader><CardTitle>Adjustments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {run.adjustments?.map((adjustment) => (
              <div key={`incoming-${adjustment.id}`} className="rounded border p-2 text-sm">
                Adjustment to Run #{adjustment.original_run} ({adjustment.original_run_pay_period_name}): {adjustment.reason}
                <span className="ml-2 font-medium">{adjustment.field_label} {adjustment.delta}</span>
              </div>
            ))}
            {activeOutgoingAdjustments.map((adjustment) => (
              <div key={adjustment.id} className="rounded border p-2 text-sm">
                {adjustment.field_label}: {adjustment.previous_value} → {adjustment.new_value} ({adjustment.delta}) — {adjustment.reason}
                <span className="ml-2 text-gray-500">
                  {adjustment.created_by_username ?? 'Unknown'} /{' '}
                  {formatDateTimeInTimeZone(adjustment.created_at, timeZone)}
                </span>
              </div>
            ))}
            {supersededAdjustments.length > 0 && (
              <div className="mt-4 space-y-2 rounded border border-dashed p-3 text-gray-500">
                <h3 className="font-medium text-gray-700">Superseded adjustments (history)</h3>
                <p className="text-sm">
                  These corrections were superseded when this run was reopened; the
                  correction was made directly in the reopened run and they are not
                  included in any export.
                </p>
                {supersededAdjustments.map((adjustment) => (
                  <div key={adjustment.id} className="rounded border border-dashed p-2 text-sm">
                    {adjustment.field_label}: {adjustment.previous_value} → {adjustment.new_value} ({adjustment.delta}) — {adjustment.reason}
                    <div>
                      Against snapshot v{adjustment.snapshot_version} · Superseded by{' '}
                      {adjustment.superseded_by_username ?? 'Unknown'} on{' '}
                      {adjustment.superseded_at
                        ? formatDateTimeInTimeZone(adjustment.superseded_at, timeZone)
                        : 'Unknown date'}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {canManagePayroll && run.is_locked && (
              <form
                className="mt-4 space-y-3 rounded border bg-gray-50 p-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!adjustmentLineItem || !adjustmentValue.trim() || !adjustmentReason.trim()) return;
                  createAdjustment.mutate({
                    runId,
                    line_item: Number(adjustmentLineItem),
                    field: adjustmentField,
                    new_value: adjustmentValue,
                    reason: adjustmentReason,
                  }, {
                    onSuccess: () => {
                      setAdjustmentValue('');
                      setAdjustmentReason('');
                    },
                  });
                }}
              >
                <p className="text-sm font-semibold">Add adjustment</p>
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="text-sm">
                    <span className="mb-1 block text-gray-600">Line item *</span>
                    <select
                      className="w-full rounded border px-2 py-2"
                      value={adjustmentLineItem}
                      onChange={(event) => setAdjustmentLineItem(event.target.value)}
                      required
                    >
                      <option value="">Select worker</option>
                      {run.line_items.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.person_name ?? item.tech_name ?? `Line item #${item.id}`} — {rateTypeLabel(item.rate_type)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm">
                    <span className="mb-1 block text-gray-600">Field</span>
                    <select
                      className="w-full rounded border px-2 py-2"
                      value={adjustmentField}
                      onChange={(event) => setAdjustmentField(event.target.value as PayrollAdjustment['field'])}
                    >
                      {[
                        ['regular_hours', 'Regular hours'],
                        ['overtime_hours', 'Overtime hours'],
                        ['regular_pay', 'Regular pay'],
                        ['overtime_pay', 'Overtime pay'],
                        ['per_diem_total', 'Per diem'],
                        ['mileage_total', 'Mileage'],
                        ['expense_reimbursement_total', 'Expense reimbursement'],
                      ].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <label className="text-sm">
                    <span className="mb-1 block text-gray-600">New value *</span>
                    <input
                      className="w-full rounded border px-2 py-2"
                      type="number"
                      step="0.01"
                      value={adjustmentValue}
                      onChange={(event) => setAdjustmentValue(event.target.value)}
                      required
                    />
                  </label>
                </div>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-600">Reason *</span>
                  <Textarea
                    placeholder="Reason for adjustment (required)"
                    value={adjustmentReason}
                    onChange={(event) => setAdjustmentReason(event.target.value)}
                    required
                  />
                </label>
                {createAdjustment.isError && (
                  <p className="text-sm text-red-700">
                    {(createAdjustment.error as { response?: { data?: { detail?: string; reason?: string[] } } }).response?.data?.detail
                      ?? 'Unable to create adjustment.'}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={createAdjustment.isPending}
                >
                  {createAdjustment.isPending ? 'Adding...' : 'Add adjustment'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {(run.is_locked || run.reopen_history?.length > 0) && (
        <Card>
          <CardHeader><CardTitle>Version history</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {run.snapshot_version && (
              <details className="rounded border p-2" open={snapshots.length === 1}>
                <summary className="cursor-pointer font-medium">
                  Current snapshot v{run.snapshot_version}
                </summary>
                {snapshots.find((snapshot) => snapshot.version === run.snapshot_version) && (
                  <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2 text-xs">
                    {JSON.stringify(
                      snapshots.find((snapshot) => snapshot.version === run.snapshot_version)?.totals,
                      null,
                      2
                    )}
                  </pre>
                )}
              </details>
            )}
            {run.reopen_history?.map((entry) => (
              <details key={entry.version} className="rounded border p-2">
                <summary className="cursor-pointer font-medium">
                  Snapshot v{entry.version} · reopened{' '}
                  {formatDateTimeInTimeZone(entry.reopened_at, timeZone)}
                </summary>
                <div className="mt-2 text-sm text-gray-600">
                  <p>
                    Reopened by {entry.reopened_by_username ?? 'Unknown'} on{' '}
                    {formatDateTimeInTimeZone(entry.reopened_at, timeZone)}: {entry.reopen_reason}
                  </p>
                  {entry.exported_at && (
                    <p>Exported {formatDateTimeInTimeZone(entry.exported_at, timeZone)}</p>
                  )}
                  {snapshots.find((snapshot) => snapshot.version === entry.version) && (
                    <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2 text-xs">
                      {JSON.stringify(
                        snapshots.find((snapshot) => snapshot.version === entry.version)?.totals,
                        null,
                        2
                      )}
                    </pre>
                  )}
                </div>
              </details>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-4">
        {[
          { label: 'Regular Pay', value: run.total_regular_pay },
          { label: 'Overtime Pay', value: run.total_overtime_pay },
          { label: 'Per Diem', value: run.total_per_diem },
          { label: 'Mileage', value: run.total_mileage_reimbursement },
          { label: 'Expenses', value: run.total_expense_reimbursement },
          { label: 'Gross Pay', value: run.gross_pay },
          { label: 'Total Reimb.', value: run.total_reimbursements },
          { label: 'Grand Total', value: run.grand_total },
        ].map((item) => (
          <Card key={item.label}>
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="text-lg font-semibold">
                {formatMoney(item.value, orgCurrency, MINIMUM_TWO_FRACTION_DIGITS)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Line Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Per-Tech Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {overtimeMethodLabel(run.overtime_rate_method) && (
            <p className="mb-3 text-sm font-medium">
              Overtime method: {overtimeMethodLabel(run.overtime_rate_method)}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 pr-4">Name</th>
                  <th className="pb-2 pr-4 text-right">Reg Hrs</th>
                  <th className="pb-2 pr-4 text-right">OT Hrs</th>
                  <th className="pb-2 pr-4 text-right">Rate</th>
                  <th className="pb-2 pr-4 text-right">Reg Pay</th>
                  <th className="pb-2 pr-4 text-right">OT Pay</th>
                  <th className="pb-2 pr-4 text-right">Per Diem</th>
                  <th className="pb-2 pr-4 text-right">Mileage</th>
                  <th className="pb-2 pr-4 text-right">Expenses</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Total Reimb.</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Gross Pay</th>
                  <th className="pb-2 text-right font-semibold">Grand Total</th>
                </tr>
              </thead>
              <tbody>
                {run.line_items.map((item) => {
                  const lineGross = Number(item.gross_pay);
                  const lineReimb = Number(item.total_reimbursements);
                  return (
                    <tr
                      key={item.id}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="py-2 pr-4">
                        <p className="font-medium">{item.person_name ?? item.tech_name ?? 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{rateTypeLabel(item.rate_type)}</p>
                        <p className="text-xs text-gray-400">{item.person_email ?? item.tech_email ?? ''}</p>
                        <JobBreakdownDisclosure rows={item.job_breakdown ?? []} currency={orgCurrency} />
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {item.rate_type === 'daily'
                          ? `${Number(item.work_days).toFixed(0)} days`
                          : item.regular_hours}
                      </td>
                      <td className="py-2 pr-4 text-right">{item.overtime_hours}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(item.regular_rate, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(item.regular_pay, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(item.overtime_pay, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(item.per_diem_total, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(item.mileage_total, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(item.expense_reimbursement_total, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(lineReimb, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right font-semibold">{formatMoney(lineGross, orgCurrency)}</td>
                      <td className="py-2 text-right font-semibold">{formatMoney(lineGross + lineReimb, orgCurrency)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold bg-gray-50">
                  <td className="py-2 pr-4">TOTALS</td>
                  <td className="py-2 pr-4" colSpan={3}></td>
                  <td className="py-2 pr-4 text-right">{formatMoney(run.total_regular_pay, orgCurrency)}</td>
                  <td className="py-2 pr-4 text-right">{formatMoney(run.total_overtime_pay, orgCurrency)}</td>
                  <td className="py-2 pr-4 text-right">{formatMoney(run.total_per_diem, orgCurrency)}</td>
                  <td className="py-2 pr-4 text-right">{formatMoney(run.total_mileage_reimbursement, orgCurrency)}</td>
                  <td className="py-2 pr-4 text-right">{formatMoney(run.total_expense_reimbursement, orgCurrency)}</td>
                  <td className="py-2 pr-4 text-right">{formatMoney(run.total_reimbursements, orgCurrency)}</td>
                  <td className="py-2 pr-4 text-right font-semibold">{formatMoney(run.gross_pay, orgCurrency)}</td>
                  <td className="py-2 text-right font-semibold">{formatMoney(run.grand_total, orgCurrency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {run.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{run.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
